import styled from '@emotion/styled/macro'
import { Box, Typography } from '@mui/material'
import theme from 'theme'

export const TableWrapper = styled(Box)`
  margin-top: 24px;
  margin-bottom: 24px;
`

// 5 rows visible (~60px each) + a little breathing room before scroll kicks in.
const VIEWPORT_MAX_HEIGHT = 320

export const TableViewport = styled(Box)`
  max-height: ${VIEWPORT_MAX_HEIGHT}px;
  overflow-y: auto;
  overflow-x: auto;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.borderMedium};
    border-radius: ${theme.attributes.borderRadius.large};
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.legacyBorderLow2};
  }

  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.borderMedium} transparent;

  html[data-theme='light'] & {
    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.strokePrimary};
    }

    scrollbar-color: ${theme.colors.strokePrimary} transparent;
  }
`

export const StyledTable = styled('table')`
  border-collapse: separate;
  border-spacing: 0 4px;
  width: 100%;
  font-size: ${theme.typography.fontSize.large};

  thead tr {
    background-color: ${theme.colors.surfaceItemBackgroundLow};
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 14px 16px;
    text-align: left;
    color: ${theme.colors.calendarDay};
    font-weight: ${theme.typography.fontWeight.normal};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    line-height: 22px;
    background-color: ${theme.colors.surfaceItemBackgroundLow};
    border-top: 1px solid ${theme.colors.borderMedium};
    border-bottom: 1px solid ${theme.colors.borderMedium};
    border-left: none;
    border-right: none;
    height: 50px;
    box-sizing: border-box;
    text-transform: none;
  }

  thead th:first-of-type {
    border-left: 1px solid ${theme.colors.borderMedium};
    border-top-left-radius: ${theme.attributes.borderRadius.tertiary};
    border-bottom-left-radius: ${theme.attributes.borderRadius.tertiary};
  }

  thead th:last-of-type {
    border-right: 1px solid ${theme.colors.borderMedium};
    border-top-right-radius: ${theme.attributes.borderRadius.tertiary};
    border-bottom-right-radius: ${theme.attributes.borderRadius.tertiary};
  }

  tbody tr:nth-of-type(odd) {
    background-color: transparent;
  }

  tbody tr:nth-of-type(even) {
    background-color: ${theme.colors.surfaceContainerBackground};
  }

  tbody td {
    padding: 14px 16px;
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    line-height: 20px;
    border-top: 1px solid ${theme.colors.borderMedium};
    border-bottom: 1px solid ${theme.colors.borderMedium};
    border-left: none;
    border-right: none;
    height: 60px;
    box-sizing: border-box;
  }

  tbody td:first-of-type {
    border-left: 1px solid ${theme.colors.borderMedium};
    border-top-left-radius: ${theme.attributes.borderRadius.tertiary};
    border-bottom-left-radius: ${theme.attributes.borderRadius.tertiary};
  }

  tbody td:last-of-type {
    border-right: 1px solid ${theme.colors.borderMedium};
    border-top-right-radius: ${theme.attributes.borderRadius.tertiary};
    border-bottom-right-radius: ${theme.attributes.borderRadius.tertiary};
  }
`

export const TableTitle = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.xLarge};
  font-weight: ${theme.typography.fontWeight.extraThick};
  color: ${theme.colors.menuItemText};
  margin-bottom: 12px;
`

export const TableFooter = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 4px 0;
`

export const RowCountLabel = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.normal};
  line-height: 22px;
  color: ${theme.colors.calendarDay};
`

export const DownloadButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 22px;
  color: ${theme.colors.cta};
  border-radius: ${theme.attributes.borderRadius.secondary};

  &:hover {
    color: ${theme.colors.cta};
    background-color: ${theme.colors.hoverPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.cta};
    outline-offset: 2px;
  }

  /* DownloadIcon bakes fill="#ffa9fc" as an SVG attribute; a CSS fill property overrides it. */
  html[data-theme='light'] & {
    svg path {
      fill: ${theme.colors.cta};
    }

    &:hover {
      background-color: ${theme.colors.cardFillTertiary};
    }
  }
`
